import React from "react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { FaqContentRowItem } from "./FaqContentRowItem";
import { FaqSortableContentRow } from "./FaqSortableContentRow";
import { faqRowSortId } from "./faqDndIds";
import { useFaqStore } from "src/store/faqStore";

type FaqSortableRowListProps = {
  sectionIndex: number;
};

export const FaqSortableRowList: React.FC<FaqSortableRowListProps> = ({
  sectionIndex,
}) => {
  const section = useFaqStore((s) => s.form?.faq[sectionIndex]);
  const canManage = useFaqStore((s) => s.canManage);
  const moveRowByIndex = useFaqStore((s) => s.moveRowByIndex);

  const rowIds =
    section?.content.map((_, rIdx) => faqRowSortId(sectionIndex, rIdx)) ?? [];

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!active || !over || active.id === over.id) return;
    const oldIndex = rowIds.indexOf(String(active.id));
    const newIndex = rowIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    moveRowByIndex(sectionIndex, oldIndex, newIndex);
  };

  if (!section) return null;

  if (!canManage) {
    return (
      <ul className="mt-4 flex flex-col gap-4">
        {section.content.map((_, rIdx) => (
          <FaqContentRowItem
            key={`${sectionIndex}-${rIdx}`}
            sectionIndex={sectionIndex}
            rowIndex={rIdx}
            isDragging={false}
          />
        ))}
      </ul>
    );
  }

  return (
    <DndContext
      id={`faq-rows-dnd-${sectionIndex}`}
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
        <ul className="mt-4 flex flex-col gap-4">
          {section.content.map((_, rIdx) => (
            <FaqSortableContentRow
              key={rowIds[rIdx]}
              id={rowIds[rIdx]}
              disabled={false}
            >
              {(sortable) => (
                <FaqContentRowItem
                  sectionIndex={sectionIndex}
                  rowIndex={rIdx}
                  isDragging={sortable.isDragging}
                  dragHandleProps={sortable.dragHandleProps}
                  setNodeRef={sortable.setNodeRef}
                  sortableStyle={sortable.style}
                />
              )}
            </FaqSortableContentRow>
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
};
